import { useId } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptimizedImage } from '@/components/ui/optimized-image';
import bookshelfImage from '@/assets/bookshelf-collection.webp';

interface HeroSectionProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSearch: (e: React.FormEvent) => void;
}

const HeroSection = ({ searchQuery, onSearchChange, onSearch }: HeroSectionProps) => {
  const searchInputId = useId();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim() === '') return;
    onSearch(e);
  };

  return (
    <section className="relative min-h-[70vh] lg:min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-[hsl(var(--hero-gradient-start,30_100%_96%))] via-[hsl(var(--hero-gradient-middle,25_100%_88%))] to-[hsl(var(--hero-gradient-end,24_95%_75%))]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6 sm:space-y-8">
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
              Where Stories
              <br />
              <span className="text-foreground">Find New Homes</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl">
              Connect with book lovers. Buy second-hand books at great prices or give your finished reads a new life.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/30 font-semibold" aria-label="Browse and buy books">
                <Link to="/books">Buy Books</Link>
              </Button>
              <Button asChild size="lg" className="w-full sm:w-auto text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 h-auto bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/30 font-semibold" aria-label="List your books for sale">
                <Link to="/sell">Sell Books</Link>
              </Button>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="max-w-xl" role="search">
              <div className="flex items-stretch gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-5 w-5 pointer-events-none z-10" aria-hidden="true" />
                  <label htmlFor={searchInputId} className="sr-only">Search for books, authors, or categories</label>
                  <Input
                    id={searchInputId}
                    type="search"
                    placeholder="Search books, authors, categories..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 h-12 bg-background/50 backdrop-blur border-border text-base placeholder:text-muted-foreground/70 placeholder:text-left text-left w-full"
                  />
                </div>
                <Button type="submit" className="h-12 px-6 flex-shrink-0">
                  Search
                </Button>
              </div>
            </form>
          </div>

          {/* Right Content - Feature Image */}
          <div className="relative lg:block hidden">
            <div className="relative w-full aspect-[3/4] max-h-[600px] rounded-3xl overflow-hidden bg-background/80 backdrop-blur-sm border border-border shadow-2xl">
              <OptimizedImage 
                src={bookshelfImage} 
                alt="Book collection on shelves" 
                aspectRatio="auto"
                className="w-full h-full"
                priority
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent flex items-end">
                <div className="text-center w-full space-y-2 p-8">
                  <p className="text-2xl font-semibold text-foreground">Your Book Collection Awaits</p>
                  <p className="text-muted-foreground">Thousands of books from trusted sellers</p>
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-72 h-72 bg-accent/20 rounded-full blur-xl" aria-hidden="true" />
            <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-primary/10 rounded-full blur-xl" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
